import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Trash, Check, Package } from "@phosphor-icons/react";

const CATEGORIES = ["calling", "messaging", "numbers", "esim", "topup", "fax", "broadcast", "other"];
const empty = { sku: "", name: "", category: "other", retail_dollars: "", markup_pct: "" };

export default function AdminCatalog() {
  const qc = useQueryClient();
  const [rowEdits, setRowEdits] = useState({});
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState("");
  const { data } = useQuery({ queryKey: ["catalog"], queryFn: async () => (await api.get("/catalog/products")).data });
  const products = data?.products || [];

  const refresh = () => qc.invalidateQueries({ queryKey: ["catalog"] });

  const saveRow = async (p) => {
    const e = rowEdits[p.sku] || {};
    const retail = e.retail_dollars !== undefined ? Math.round(parseFloat(e.retail_dollars) * 100) : p.retail_price_cents;
    const markup = e.markup_pct !== undefined ? parseFloat(e.markup_pct) : p.markup_pct;
    if (!(retail >= 0)) { toast.error("Enter a valid price"); return; }
    setBusy(p.sku);
    try {
      await api.post("/admin/products", { sku: p.sku, name: e.name ?? p.name, category: e.category ?? p.category,
        retail_price_cents: retail, markup_pct: markup || 0, active: e.active ?? p.active ?? true });
      toast.success("Saved"); setRowEdits((x) => { const n = { ...x }; delete n[p.sku]; return n; }); refresh();
    } catch { toast.error("Could not save"); } finally { setBusy(""); }
  };

  const del = async (sku) => {
    if (!window.confirm(`Delete product ${sku}?`)) return;
    setBusy(sku);
    try { await api.delete(`/admin/products/${encodeURIComponent(sku)}`); toast.success("Deleted"); refresh(); }
    catch { toast.error("Could not delete"); } finally { setBusy(""); }
  };

  const add = async () => {
    if (!form.sku.trim() || !form.name.trim()) { toast.error("SKU and name are required"); return; }
    const retail = Math.round(parseFloat(form.retail_dollars || "0") * 100);
    if (!(retail >= 0)) { toast.error("Enter a valid price"); return; }
    setBusy("__add");
    try {
      await api.post("/admin/products", { sku: form.sku.trim(), name: form.name.trim(), category: form.category,
        retail_price_cents: retail, markup_pct: parseFloat(form.markup_pct || "0"), active: true });
      toast.success("Product added"); setForm(empty); refresh();
    } catch (e) { toast.error("Could not add product"); } finally { setBusy(""); }
  };

  const setEdit = (sku, k, v) => setRowEdits((x) => ({ ...x, [sku]: { ...(x[sku] || {}), [k]: v } }));

  return (
    <div className="max-w-4xl space-y-6" data-testid="admin-catalog">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">Products &amp; Pricing</h1>
        <p className="mt-1 text-sm text-muted-foreground">Add your own products and set retail prices. Wholesale cost stays hidden from customers.</p>
      </div>

      {/* Add product */}
      <div className="rounded-2xl border border-white/10 bg-card p-4" data-testid="add-product">
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold"><Plus size={16} weight="bold" /> Add a product / service</h2>
        <div className="grid gap-2 sm:grid-cols-6">
          <input data-testid="new-sku" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} placeholder="SKU" className="rounded-lg border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
          <input data-testid="new-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Product name" className="sm:col-span-2 rounded-lg border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
          <select data-testid="new-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-lg border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input data-testid="new-price" type="number" min="0" step="0.01" value={form.retail_dollars} onChange={(e) => setForm({ ...form, retail_dollars: e.target.value })} placeholder="$ price" className="rounded-lg border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
          <input data-testid="new-markup" type="number" min="0" step="1" value={form.markup_pct} onChange={(e) => setForm({ ...form, markup_pct: e.target.value })} placeholder="markup %" className="rounded-lg border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
        </div>
        <button data-testid="add-product-btn" onClick={add} disabled={busy === "__add"} className="mt-3 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50">Add product</button>
      </div>

      {/* List */}
      <div className="overflow-hidden rounded-2xl border border-white/10 bg-card">
        <div className="grid grid-cols-12 gap-2 border-b border-white/10 px-4 py-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          <div className="col-span-2">SKU</div><div className="col-span-3">Name</div><div className="col-span-2">Category</div>
          <div className="col-span-2 text-right">Retail $</div><div className="col-span-1 text-right">Markup%</div><div className="col-span-2 text-right">Actions</div>
        </div>
        {products.map((p) => {
          const e = rowEdits[p.sku] || {};
          const dirty = Object.keys(e).length > 0;
          return (
            <div key={p.sku} data-testid={`product-${p.sku}`} className="grid grid-cols-12 items-center gap-2 border-b border-white/5 px-4 py-2.5 text-sm last:border-0">
              <div className="col-span-2 font-mono-metric text-xs">{p.sku}</div>
              <input data-testid={`edit-name-${p.sku}`} value={e.name ?? p.name} onChange={(ev) => setEdit(p.sku, "name", ev.target.value)} className="col-span-3 rounded border border-transparent bg-transparent px-2 py-1 text-sm hover:border-white/10 focus:border-primary focus:outline-none" />
              <select data-testid={`edit-cat-${p.sku}`} value={e.category ?? p.category} onChange={(ev) => setEdit(p.sku, "category", ev.target.value)} className="col-span-2 rounded bg-background px-2 py-1 text-xs">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input data-testid={`edit-price-${p.sku}`} type="number" min="0" step="0.01" value={e.retail_dollars ?? (p.retail_price_cents / 100)} onChange={(ev) => setEdit(p.sku, "retail_dollars", ev.target.value)} className="col-span-2 rounded border border-white/10 bg-background px-2 py-1 text-right text-sm focus:border-primary focus:outline-none" />
              <input data-testid={`edit-markup-${p.sku}`} type="number" min="0" value={e.markup_pct ?? p.markup_pct} onChange={(ev) => setEdit(p.sku, "markup_pct", ev.target.value)} className="col-span-1 rounded border border-white/10 bg-background px-2 py-1 text-right text-sm focus:border-primary focus:outline-none" />
              <div className="col-span-2 flex items-center justify-end gap-1.5">
                <button data-testid={`save-${p.sku}`} onClick={() => saveRow(p)} disabled={!dirty || busy === p.sku} className="flex items-center gap-1 rounded-lg bg-primary px-2.5 py-1.5 text-xs font-semibold text-primary-foreground disabled:opacity-40"><Check size={12} weight="bold" /></button>
                <button data-testid={`delete-${p.sku}`} onClick={() => del(p.sku)} disabled={busy === p.sku} className="rounded-lg border border-white/10 p-1.5 text-red-400 hover:bg-red-500/10"><Trash size={14} /></button>
              </div>
            </div>
          );
        })}
        {products.length === 0 && <p className="flex items-center justify-center gap-2 px-4 py-10 text-center text-sm text-muted-foreground"><Package size={16} /> No products yet — add one above.</p>}
      </div>
    </div>
  );
}
